import { AxiosResponse } from "axios"

export enum ErrorDisplay {
    DIALOG = "DIALOG",
    SNACKBAR = "SNACKBAR",
    ROUTE = "ROUTE",
    NONE = "NONE",
}

export enum ErrorMatchType {
    ERROR_CODE = "ERROR_CODE",
    RESPONSE_STATUS = "RESPONSE_STATUS",
    API_STATUS = "API_STATUS",
}

type BaseErrorMatcher = Readonly<{
    display?: ErrorDisplay
    message?: string
    skipDefaultAction?: boolean
}>

type ErrorCodeMatcher = BaseErrorMatcher &
    Readonly<{
        type: "error-code"
        match: string
    }>

type ResponseStatusMatcher = BaseErrorMatcher &
    Readonly<{
        type: "response-status"
        match: string | number
    }>

type ApiStatusMatcher = BaseErrorMatcher &
    Readonly<{
        type: "api-status"
        match: string
    }>

export type ErrorMatcher = ErrorCodeMatcher | ResponseStatusMatcher | ApiStatusMatcher

export type AppErrorType = Readonly<{
    name: string
    code: string
    title: string
    displayMessage: string
    status?: number
    statusText?: string
    apiStatus?: string
    hasApiMessage?: boolean
}>

export class AppError extends Error {
    readonly code: string
    readonly title: string
    readonly displayMessage: string
    readonly cause?: Error

    constructor(code: string, title: string, message: string, cause?: Error) {
        super(`App error: ${message}`)

        // Prettier logging
        Object.defineProperty(this, "name", {
            value: new.target.name,
            enumerable: false,
            configurable: true,
        })

        this.code = code
        this.title = title
        this.displayMessage = message
        this.cause = cause

        // Fix up prototype chain
        // See: https://www.typescriptlang.org/docs/handbook/release-notes/typescript-2-2.html#support-for-newtarget
        Object.setPrototypeOf(this, new.target.prototype)
    }

    toObject(): AppErrorType {
        return {
            name: this.name,
            code: this.code,
            title: this.title,
            displayMessage: this.displayMessage,
        }
    }

    static unwrap(error: unknown) {
        if (error instanceof AppError && error.cause) {
            return error.cause
        } else {
            return error
        }
    }

    static wrap(error: unknown) {
        if (error instanceof AppError) {
            return error
        } else if (error instanceof Error) {
            return new AppError("E_UNKNOWN_ERROR", error.name, error.message, error)
        } else {
            return new AppError("E_UNKNOWN_ERROR", "Unknown error", "Something went wrong")
        }
    }
}

export class ApiError extends AppError {
    readonly status?: number
    readonly statusText?: string
    readonly apiStatus?: string
    readonly hasApiMessage: boolean
    readonly originalErrorResponse?: AxiosResponse

    constructor(
        code: string,
        title: string,
        message: string,
        hasApiMessage: boolean,
        status?: number,
        statusText?: string,
        apiStatus?: string,
        cause?: Error,
        originalErrorResponse?: AxiosResponse,
    ) {
        super(code, title, message, cause)

        // Prettier logging
        Object.defineProperty(this, "name", {
            value: new.target.name,
            enumerable: false,
            configurable: true,
        })

        this.status = status
        this.statusText = statusText
        this.apiStatus = apiStatus
        this.hasApiMessage = hasApiMessage
        this.originalErrorResponse = originalErrorResponse

        // Fix up prototype chain
        // See: https://www.typescriptlang.org/docs/handbook/release-notes/typescript-2-2.html#support-for-newtarget
        Object.setPrototypeOf(this, new.target.prototype)
    }

    toObject(): AppErrorType {
        const base = super.toObject()
        return {
            ...base,
            status: this.status,
            statusText: this.statusText,
            apiStatus: this.apiStatus,
            hasApiMessage: this.hasApiMessage,
        }
    }
}

export class NoDataError extends AppError {
    constructor() {
        super("E_NO_DATA", "No data", "No data available")

        Object.defineProperty(this, "name", {
            value: new.target.name,
            enumerable: false,
            configurable: true,
        })

        // Fix up prototype chain
        // See: https://www.typescriptlang.org/docs/handbook/release-notes/typescript-2-2.html#support-for-newtarget
        Object.setPrototypeOf(this, new.target.prototype)
    }
}

export type DisplayError = Readonly<{
    display: ErrorDisplay
    error: AppErrorType
}>

export type ErrorState = Readonly<{
    queue: DisplayError[]
    current: DisplayError | undefined
}>
